import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { fromEvent, merge } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
import { LayoutUtilsService, QueryParamsModel } from '../../../../../../core/_base/crud';
import {
  Drug,
  DrugOnServerDeleted,
  DrugOnServerUpdated,
  DrugsDataSource,
  DrugsPageRequested,
  ViewContext,
} from '../../../../../../core/lists';
import { TranslateService } from '@ngx-translate/core';
import { Update } from '@ngrx/entity';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NavigationService } from '../../../../../../core/services/navigation.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { TabletUtil } from '../../../../../../core/utils';

@Component({
  selector: 'kt-drugs-list',
  templateUrl: './drugs-list.component.html',
  styleUrls: ['./drugs-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrugsListComponent implements OnInit, OnDestroy {
  dataSource: DrugsDataSource;
  displayedColumns = ['drug', 'ndc', 'thClass', 'jcode', 'isActive', 'actions'];
  searchBarColumns = [
    'searchDrug',
    'searchNDC',
    'searchTHClass',
    'searchJCode',
    'searchIsActive',
    'searchActions',
  ];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  searchDrugInput = '';
  searchNDCInput = '';
  searchTHClassInput = '';
  searchJCodeInput = '';
  searchIsActiveSelect = 'true';
  statusTracker = 0;
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  private subscriptions: any[] = [];
  searchForm: FormGroup;
  filtersApplied: any = false;
  firstLoadAfterBack: any = false;
  pageSize: any;

  constructor(
    private navigationService: NavigationService,
    private permissionsService: NgxPermissionsService,
    private tabletUtil: TabletUtil,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private layoutUtilsService: LayoutUtilsService,
    private store: Store<AppState>,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    // Comment by Nourhan: please don't change the order of this line
    this.filtersApplied = this.navigationService.getFilters();

    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = this.paginator?.pageIndex || 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'id';
      this.sort.direction = this.sort.direction || 'desc';
    });
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => {
          this.loadDrugsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });

    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadDrugsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 3, sub: searchSubscription });

    this.dataSource = new DrugsDataSource(this.store);

    this.searchForm = this.fb.group({
      search: [],
      status: ['true'],
    });

    // Comment by Nourhan: please don't change the order of this scope
    if (this.filtersApplied) {
      this.firstLoadAfterBack = true;
      this.pageSize = this.filtersApplied.pageSize;
    }

    const routeSubscription = this.activatedRoute.queryParams.subscribe(() => {
      this.loadDrugsList();
    });
    this.subscriptions.push({ id: 5, sub: routeSubscription });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  loadDrugsList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'drug',
      this.paginator.pageIndex,
      this.paginator.pageSize || this.pageSize || 25,
    );
    this.store.dispatch(new DrugsPageRequested({ page: queryParams }));
  }

  filterConfiguration(reset_applied_flag?: any): any {
    const filter: any = {};
    filter.isActive = this.searchIsActiveSelect;
    if (this.filtersApplied && this.firstLoadAfterBack) {
      this.firstLoadAfterBack = false;
      const appliedFilters = this.filtersApplied;
      filter.isActive = appliedFilters.isActive;
      this.searchIsActiveSelect = appliedFilters.isActive;
      this.showSearchBar = appliedFilters.showSearchBar;
      if (this.showSearchBar) {
        this.searchDrugInput = appliedFilters.drug;
        this.searchNDCInput = appliedFilters.ndc;
        this.searchTHClassInput = appliedFilters.th_class;
        this.searchJCodeInput = appliedFilters.jcode;
      } else {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = appliedFilters.searchTerm || '';
        }
      }
      this.sort.direction = this.filtersApplied?.sortDirection || 'desc';
      this.sort.active = this.filtersApplied?.sortActive || 'drug';
      this.paginator.pageIndex = this.filtersApplied?.pageIndex || 0;
      this.paginator.pageSize = this.filtersApplied?.pageSize || 25;
    }
    if (reset_applied_flag) this.filtersApplied = false;
    if (this.showSearchBar) {
      filter.drug = this.searchDrugInput;
      filter.ndc = this.searchNDCInput;
      filter.th_class = this.searchTHClassInput;
      filter.jcode = this.searchJCodeInput;
      filter.showSearchBar = true;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
      filter.showSearchBar = false;
    }
    filter.sortDirection = this.sort.direction || 'desc';
    filter.sortActive = this.sort.active || 'drug';
    filter.pageIndex = this.paginator.pageIndex;
    filter.pageSize = this.paginator.pageSize;
    return filter;
  }

  deleteDrug(_item: Drug) {
    const _title: string = this.translate.instant('DRUG.DELETE_DRUG_SIMPLE.TITLE');
    const _description: string = this.translate.instant('DRUG.DELETE_DRUG_SIMPLE.DESCRIPTION');
    const _waitDescription: string = this.translate.instant(
      'DRUG.DELETE_DRUG_SIMPLE.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDescription);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.store.dispatch(new DrugOnServerDeleted({ id: _item.id }));
    });
  }

  updateStatusForDrug(drug: Drug) {
    const _title: string = this.translate.instant('DRUG.UPDATE_STATUS_SIMPLE.TITLE');
    const _description: string = this.translate.instant('DRUG.UPDATE_STATUS_SIMPLE.DESCRIPTION');
    const _waitDescription: string = this.translate.instant(
      'DRUG.UPDATE_STATUS_SIMPLE.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, _waitDescription);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      const newDrug: Drug = Object.assign(new Drug(), drug);
      newDrug.changeActiveStatus();
      const updateDrug: Update<Drug> = {
        id: newDrug.id,
        changes: newDrug,
      };
      this.store.dispatch(
        new DrugOnServerUpdated({
          partialDrug: updateDrug,
          drug: newDrug,
        }),
      );
    });
  }
  

  getDrug(drug: Drug) {
    let result: string;
    if (drug.ln60) {
      result = drug.ln60;
      return result;
    }
    const name = drug.drug ? drug.drug : '';
    const strength = drug.strength ? drug.strength : '';
    const form = drug.form ? drug.form : '';
    result = name + ' ' + strength + ' ' + form;
    return result;
  }

  canViewDrug(): boolean {
    return !!this.permissionsService.getPermission('canViewDrug');
  }

  canEditDrug(): boolean {
    return !!this.permissionsService.getPermission('canEditDrug');
  }

  createDrug() {
    this.navigationService.navigateTo('lists/drugs/add');
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  viewDrug(drug: Drug) {
    if (!this.canViewDrug()) return;

    if (this.canEditDrug()) {
      this.editDrug(drug.id);
      return;
    }
    const queryParams = {
      viewContext: ViewContext.drugs,
    };
    this.navigationService.navigateTo('lists/drugs/view/' + drug.id, queryParams);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  viewDrugDblclick(ref: string, drug: Drug): void {
    this.tabletUtil.handleDblclick(ref, this.viewDrug.bind(this, drug));
  }

  editDrug(id: number) {
    const queryParams = {
      viewContext: ViewContext.drugs,
    };

    this.navigationService.navigateTo('lists/drugs/edit/' + id, queryParams);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  getItemCssClassByStatus(status: string): string {
    switch (status) {
      case 'false':
        return 'danger';
      case 'true':
        return 'success';
      default:
        return 'warning';
    }
  }

  getItemStatusString(status: string): string {
    switch (status) {
      case 'false':
        return this.translate.instant('GLOBAL.INACTIVE');
      case 'true':
        return this.translate.instant('GLOBAL.ACTIVE');
      default:
        return this.translate.instant('GLOBAL.ALL');
    }
  }

  reloadDrugsList(): void {
    this.paginator.pageIndex = 0;
    this.loadDrugsList();
  }

  toggleStatus(event: Event): void {
    event.stopPropagation();

    this.statusTracker = (this.statusTracker + 1) % 3;

    switch (this.statusTracker) {
      case 0:
        this.searchIsActiveSelect = 'true';
        break;
      case 1:
        this.searchIsActiveSelect = 'false';
        break;
      case 2:
        this.searchIsActiveSelect = '';
        break;
    }

    this.reloadDrugsList();
  }

  handleStatusChange(): void {
    switch (this.searchIsActiveSelect) {
      case 'true':
        this.statusTracker = 0;
        break;
      case 'false':
        this.statusTracker = 1;
        break;
      case '':
        this.statusTracker = 2;
        break;
    }
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();
    this.searchDrugInput = '';
    this.searchNDCInput = '';
    this.searchTHClassInput = '';
    this.searchJCodeInput = '';
  }

  resetSearchInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadDrugsList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
    } else {
      this.resetSearchBar();
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadDrugsList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();
    this.reloadDrugsList();
  }
}
